import * as React from 'react'
import { useEffect, useState, useContext } from 'react'
import { EventsType, useIdleTimer } from 'react-idle-timer'
import { KeDoContext } from '../../kedo-context.js'
import Kedo from '../../Kedo.js'
import IdleTimeoutPrompt from './IdleTimeoutPrompt.jsx'
import LoginModal from '../LoginModal'

interface TimeoutProps {
    props: any
    toggleBlur: any
    handleLogin: (data: any, callBack: any, idleTimer: any) => void
    onIdleFlowStateChange?: (isActive: boolean) => void
}

const Timeout = ({
    handleLogin,
    toggleBlur,
    onIdleFlowStateChange,
}: TimeoutProps) => {
    const kedo: Kedo = useContext(KeDoContext)
    const debounce = 250
    const crossTab = true
    const syncTimers = 50
    const timeout = 1000 * 60 * 15
    const promptBeforeIdle = 1000 * 60 * 5

    //Enable for focus
    const events: EventsType[] = [
        'mousemove',
        'keydown',
        'wheel',
        'DOMMouseScroll',
        'mousewheel',
        'mousedown',
        'touchstart',
        'touchmove',
        'MSPointerDown',
        'MSPointerMove',
        'visibilitychange',
        'focus',
    ]

    // Modal open state
    const [open, setOpen] = useState(false)
    const [showLoginModal, setShowLoginModal] = useState(false)
    const [remaining, setRemaining] = useState(0)

    useEffect(
        () => () => onIdleFlowStateChange?.(false),
        [onIdleFlowStateChange]
    )

    const handleLogout = () => {
        const tokenData = localStorage.getItem('tokenData')
        const data = tokenData ? JSON.parse(tokenData) : null
        const lastVisit = data?.iat
            ? new Date(parseInt(data.iat) * 1000).getTime().toString()
            : new Date().getTime().toString()

        const clearSession = () => {
            localStorage.clear()
            localStorage.setItem('lastVisit', lastVisit)
        }

        if (!tokenData) {
            clearSession()
            return
        }

        kedo.api().get('/logout_cookie').finally(clearSession)
    }

    const isIgnoredOnPage = () => {
        return (
            window.location.pathname === '/login' ||
            window.location.pathname === '/forgot_password'
        )
    }

    const closePrompt = (resetTimeout = null) => {
        setOpen(false)
        setRemaining(0)
        setShowLoginModal(false)
        onIdleFlowStateChange?.(false)
        toggleBlur ? toggleBlur?.disable() : null
        resetTimeout ? reset() : null
    }

    const onPrompt = () => {
        if (isIgnoredOnPage()) {
            return
        }
        toggleBlur ? toggleBlur?.enable() : null
        setOpen(true)
        setRemaining(promptBeforeIdle)
        onIdleFlowStateChange?.(true)

        if (kedo) {
            kedo.api().checkTokenForExpiryAndRenew()
        }
    }

    const onIdle = () => {
        if (isIgnoredOnPage()) {
            return
        }
        handleLogout()
        setOpen(false)
        setRemaining(0)
        toggleBlur ? toggleBlur?.enable() : null
        setShowLoginModal(true)
        onIdleFlowStateChange?.(true)
    }

    const onActive = () => {
        if (open) {
            closePrompt(true)
        }
    }

    const onAction = () => {
        if (open === true && isIgnoredOnPage()) {
            closePrompt(true)
        }

        if (open === true && !isIgnoredOnPage()) {
            closePrompt(true)
            if (kedo) {
                kedo.api().checkTokenForExpiryAndRenew()
            }
        }
    }

    //This gets created too soon?
    //Todo: need to create later
    const { getRemainingTime, isPrompted, reset } = useIdleTimer({
        timeout,
        promptBeforeIdle,
        debounce,
        crossTab,
        syncTimers,
        events,
        onPrompt,
        onIdle,
        onActive,
        onAction,
    })

    useEffect(() => {
        const interval = setInterval(() => {
            if (isPrompted()) {
                setRemaining(Math.ceil(getRemainingTime() / 1000))
            }
        }, 1000)
        return () => {
            clearInterval(interval)
        }
    }, [getRemainingTime, isPrompted])

    useEffect(() => {
        const interval = setInterval(() => {
            if (isIgnoredOnPage()) {
                closePrompt(true)
            }
        }, 1000)
        return () => {
            clearInterval(interval)
        }
    }, [open])

    useEffect(() => {
        const interval = setInterval(() => {
            if (!kedo.user().isLoggedIn()) {
                return
            }

            kedo.api().checkTokenForExpiryAndRenew()
        }, 300000)
        return () => {
            clearInterval(interval)
        }
    }, [])

    //This does not work if we waited too long and the original token is expired!
    useEffect(() => {
        window.addEventListener('focus', () =>
            kedo.api().checkTokenForExpiryAndRenew()
        )

        return () => {
            window.removeEventListener('focus', () =>
                kedo.api().checkTokenForExpiryAndRenew()
            )
        }
    }, [])

    return (
        <>
            {open && !isIgnoredOnPage() && !showLoginModal ? (
                <IdleTimeoutPrompt
                    kedo={kedo}
                    promptTimeout={promptBeforeIdle}
                    remaining={remaining}
                />
            ) : null}
            {showLoginModal && !isIgnoredOnPage() ? (
                <LoginModal
                    open={showLoginModal}
                    handleLogin={(data) => {
                        try {
                            // Stay on the current route after idle relogin; do not force /environment.
                            handleLogin(data, null, reset)
                            setShowLoginModal(false)
                            onIdleFlowStateChange?.(false)
                            toggleBlur?.disable?.()
                        } catch (err) {
                            setShowLoginModal(true)
                            onIdleFlowStateChange?.(true)
                        }
                    }}
                />
            ) : null}
        </>
    )
}

export default Timeout
